import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Inject } from '@angular/core';
import { isPanelClosable, PanelModel } from '../../../panel.model';
import { WorkspaceSecurityContextService } from 'projects/shared-components/workspace/workspace-security-context.service';
import { PanelAliasesService } from '../../../panel-aliases/panel-aliases.service';

@Component({
   selector: 'ets-tab-header',
   templateUrl: './tab-header.component.html',
   styleUrls: ['./tab-header.component.scss'],
   providers: [WorkspaceSecurityContextService]
})
export class EtsTabHeaderComponent implements OnInit, AfterViewInit {
   constructor(
      readonly securityContext: WorkspaceSecurityContextService,
      private readonly _selfRef: ElementRef,
      private readonly _aliases: PanelAliasesService
   ) {
      this.tabClose = new EventEmitter<PanelModel>();
      this.addedToPanel = new EventEmitter<{ tab: PanelModel, newWidth: number }>();
      this.headerChanged = new EventEmitter<PanelModel>();
      this.symbolLinkChanged = new EventEmitter<boolean>();
   }

   @Output() tabClose;
   @Output() symbolLinkChanged: EventEmitter<boolean>;
   @Output() addedToPanel;
   @Output() headerChanged: EventEmitter<PanelModel>;
   @Input() tab: PanelModel;

   isEditing = false;

   /** CR-20260928-002: own name → alias → full name. */
   get displayName(): string {
      return this._aliases.displayName(this.tab);
   }

   /** What the tab falls back to when the editor is left empty (alias if set, else the full name). */
   get placeholder(): string {
      if (!this.tab) {
         return '';
      }
      return this._aliases.getAlias(this.tab.typeCode) || this.tab.header;
   }


   ngOnInit() { }

   get canClose(): boolean {
      return this.securityContext.canClosePanel && isPanelClosable(this.tab);
   }

   onCloseClick(event?: MouseEvent): void {
      event?.stopPropagation();
      if (!this.canClose) {
         return;
      }
      this.tabClose.emit(this.tab);
   }

   ngAfterViewInit() {
      const sr = this._selfRef.nativeElement.offsetWidth;
      this.addedToPanel.emit({ tab: this.tab, newWidth: sr });
   }

   onDoubleClick(ev): void {
      this.isEditing = !this.isEditing;
   }

   onKeyUp(ev) {
      if (ev.event.key === 'Escape') {
         this.isEditing = false;
      }
   }

   /** Blur / value commit. */
   onTextChanged(ev) {
      this.applyOwnName(ev.value);
   }

   /** Enter with an UNCHANGED value never raises onValueChanged (the editor is pre-filled now), so
    *  Enter commits explicitly. Both paths end in the same place. */
   onEnterKey(ev) {
      this.applyOwnName(ev.component.option('value'));
   }

   // CR-20260928-002 (R3/R4): the own name always wins over the alias; empty = back to the alias
   // (or the full name). `header` keeps the default and is never touched here.
   private applyOwnName(raw: any) {
      if (!this.isEditing) {
         return;
      }
      const own = (typeof raw === 'string' ? raw : '').trim();
      this.tab.customHeader = own ? own : undefined;
      this.isEditing = false;
      this.headerChanged.emit(this.tab);
   }

   onSymbolLinkedClicked() {
      this.tab.isLinkedToSymbol = !this.tab.isLinkedToSymbol;
      this.symbolLinkChanged.emit(this.tab.isLinkedToSymbol);
   }
}
